/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {MarkType} from 'prosemirror-model';
import {EditorState} from 'prosemirror-state';
import {Command, toggleMark} from 'prosemirror-commands';
import {isMarkActive} from '../../../helpers';
import {removeLink} from '../../../commands';
import isImageSelected from '../../../utils/isImageSelected';
import {Dispatch} from './types';

const defaultOptions = {
    strict: true
};

export interface LinkAttrs {
    href: string;
    title?: string;
    target?: string;
}

class Link {
    /**
     * Update link function.
     * 
     * @param attrs Attributes.
     * @returns Command.
     */
    update(attrs: LinkAttrs): Command {
        return (state: EditorState, dispatch?: Dispatch): boolean => {
            const {schema, selection} = state;

            const type: MarkType = schema.marks.link;
            if (!type) {
                return false;
            }

            if (selection.empty) {
                return false;
            }

            return toggleMark(type, attrs)(state, dispatch);
        };
    }

    /**
     * Insert link function.
     * 
     * @param text Link text.
     * @param attrs Link attributes.
     * @returns Command.
     */
    insert(text: string, attrs: LinkAttrs): Command {
        return (state: EditorState, dispatch?: Dispatch): boolean => {
            const {schema, tr} = state;

            const type: MarkType = schema.marks.link;
            if (!type) {
                return false;
            }

            const linkAttrs: LinkAttrs = {
                href: attrs.href,
                title: attrs.title ?? text,
                target: attrs.target ?? '_blank'
            };

            const node = schema.text(text, [schema.marks.link.create(linkAttrs)]);

            tr.replaceSelectionWith(node, false)
                .scrollIntoView();

            if (tr.docChanged) {
                dispatch?.(tr);
                return true;
            }

            return false;
        };
    }

    /**
     * Is link active.
     * 
     * @param state EditorState.
     * @param options Options.
     * @returns Boolean.
     */
    isActive(state: EditorState, options = defaultOptions): boolean {
        if (options.strict) {
            return true;
        }

        const {schema} = state;
        const type = schema.marks.link;

        if (!type) {
            return false;
        }

        return isMarkActive(state, type);
    }

    /**
     * Remove link.
     * 
     * @param state EditorState.
     * @param dispatch Dispatch.
     * @returns Boolean.
     */
    remove(state: EditorState, dispatch?: Dispatch): boolean {
        return removeLink()(state, dispatch);
    }
    
    /**
     * Can links execute.
     * 
     * @param state EditorState.
     * @returns Boolean.
     */
    canExecute(state: EditorState): boolean {
        const testAttrs: LinkAttrs = {
            href: ''
        };

        return !isImageSelected()(state) && 
            (this.insert('Exec', testAttrs)(state) || this.update(testAttrs)(state));
    }
}

export default Link;
